import React, { forwardRef } from 'react';
import {
  FormControl,
  Select as MuiSelect,
  MenuItem,
  InputLabel,
} from '@material-ui/core';
import { useDsTokens } from '../theme/useDsTokens';
import { cn } from '../utils/cn';
import { DsCommonProps, DsValidationProps, SelectOption } from './types';

export interface SelectProps extends DsCommonProps, DsValidationProps {
  value?: string | number;
  defaultValue?: string | number;
  name?: string;
  placeholder?: string;
  options: SelectOption[];
  fullWidth?: boolean;
  displayEmpty?: boolean;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  onChange?: (
    event: React.ChangeEvent<{ name?: string; value: unknown }>,
    child: React.ReactNode
  ) => void;
  onBlur?: React.FocusEventHandler<{ name?: string; value: unknown }>;
}

export const Select = forwardRef<HTMLDivElement, SelectProps>(function Select(
  {
    id,
    className,
    disabled,
    error,
    required,
    value,
    defaultValue,
    name,
    placeholder,
    options,
    fullWidth = true,
    displayEmpty = true,
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid,
    onChange,
    onBlur,
    'data-testid': testId,
  },
  ref
) {
  const tokens = useDsTokens();

  return (
    <FormControl
      fullWidth={fullWidth}
      disabled={disabled}
      required={required}
      error={error}
      className={cn(className)}
      data-testid={testId}
    >
      {placeholder && (
        <InputLabel shrink={displayEmpty} style={{ display: 'none' }}>
          {placeholder}
        </InputLabel>
      )}
      <MuiSelect
        ref={ref}
        id={id}
        name={name}
        value={value ?? ''}
        defaultValue={defaultValue}
        displayEmpty={displayEmpty}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid ?? (error ? true : undefined)}
        onChange={onChange}
        onBlur={onBlur}
        style={{
          backgroundColor: tokens.surfaceInput,
          borderRadius: tokens.radiusMd,
          fontSize: tokens.fontSizeBody,
          color: tokens.textPrimary,
          minHeight: tokens.space5,
        }}
        MenuProps={{
          PaperProps: {
            style: {
              backgroundColor: tokens.surfaceRaised,
              borderRadius: tokens.radiusMd,
            },
          },
        }}
      >
        {placeholder && (
          <MenuItem value="" disabled={required}>
            <em style={{ color: tokens.textSecondary, fontStyle: 'normal' }}>
              {placeholder}
            </em>
          </MenuItem>
        )}
        {options.map((option) => (
          <MenuItem
            key={String(option.value)}
            value={option.value}
            disabled={option.disabled}
            style={{
              fontSize: tokens.fontSizeBody,
              color: option.disabled ? tokens.textDisabled : tokens.textPrimary,
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </MuiSelect>
    </FormControl>
  );
});

Select.displayName = 'Select';
